import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase/config';
import { multiFactor, PhoneAuthProvider, PhoneMultiFactorGenerator, RecaptchaVerifier, onAuthStateChanged, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { enrollPasskey, isWebAuthnSupported, listPasskeys } from '../services/webauthn.service';
import { getViewerTimezone } from '../utils/dateTime';
import './SettingsPage.css';

const OTP_LENGTH = 6;
const RESEND_TIMER_SECONDS = 60;

const COUNTRIES = [
  { code: '+1',   flag: '🇺🇸', name: 'United States' },
  { code: '+1',   flag: '🇨🇦', name: 'Canada' },
  { code: '+44',  flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+61',  flag: '🇦🇺', name: 'Australia' },
  { code: '+91',  flag: '🇮🇳', name: 'India' },
  { code: '+52',  flag: '🇲🇽', name: 'Mexico' },
  { code: '+49',  flag: '🇩🇪', name: 'Germany' },
  { code: '+33',  flag: '🇫🇷', name: 'France' },
  { code: '+55',  flag: '🇧🇷', name: 'Brazil' },
  { code: '+81',  flag: '🇯🇵', name: 'Japan' },
  { code: '+82',  flag: '🇰🇷', name: 'South Korea' },
  { code: '+86',  flag: '🇨🇳', name: 'China' },
  { code: '+34',  flag: '🇪🇸', name: 'Spain' },
  { code: '+39',  flag: '🇮🇹', name: 'Italy' },
  { code: '+31',  flag: '🇳🇱', name: 'Netherlands' },
  { code: '+46',  flag: '🇸🇪', name: 'Sweden' },
  { code: '+47',  flag: '🇳🇴', name: 'Norway' },
  { code: '+41',  flag: '🇨🇭', name: 'Switzerland' },
  { code: '+65',  flag: '🇸🇬', name: 'Singapore' },
  { code: '+971', flag: '🇦🇪', name: 'UAE' },
  { code: '+972', flag: '🇮🇱', name: 'Israel' },
  { code: '+63',  flag: '🇵🇭', name: 'Philippines' },
  { code: '+64',  flag: '🇳🇿', name: 'New Zealand' },
  { code: '+353', flag: '🇮🇪', name: 'Ireland' },
  { code: '+351', flag: '🇵🇹', name: 'Portugal' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const maskEmail = (email) => {
  if (!email) return '';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  return `${local[0]}***@${domain}`;
};

const maskPhone = (phone) => {
  if (!phone) return '';
  if (phone.length <= 4) return phone;
  return `${phone.slice(0, 3)}***${phone.slice(-4)}`;
};

// Exact mirror of user-service SecuritySettingsService.maskPhoneNumber — the only
// form security-status returns the saved number in (`maskedPhone`). maskPhone above
// is display-only and formats +1 numbers differently ("+15***1234" vs "+1***1234"),
// so it can't be used to compare a Firebase-enrolled number with the backend's.
const maskPhoneLikeBackend = (phone) => {
  if (!phone || phone.length < 5) return null;
  const last4 = phone.slice(-4);
  if (phone.startsWith('+1') && phone.length > 6) return `+1***${last4}`;
  if (phone.startsWith('+') && phone.length > 4) {
    return `${phone.slice(0, Math.min(3, phone.length - 4))}***${last4}`;
  }
  return `***${last4}`;
};

const normalizePhone = (phone) => (phone || '').replace(/[^\d+]/g, '');

// Phone numbers currently enrolled as Firebase MFA second factors (E.164). Only
// meaningful after user.reload() — fetchSecurityStatus/handleSendPhoneOtp do that.
const getEnrolledMfaPhones = (user) => {
  if (!user) return [];
  try {
    return multiFactor(user).enrolledFactors
      .filter(f => f.factorId === 'phone' && f.phoneNumber)
      .map(f => normalizePhone(f.phoneNumber));
  } catch {
    return [];
  }
};

const parsePhone = (phone) => {
  const cleaned = phone.replace(/[^0-9+]/g, '');
  if (cleaned.startsWith('+1') && cleaned.length >= 12) {
    return { code: '+1', local: cleaned.slice(2) };
  }
  const match = cleaned.match(/^\+(\d{1,2})(\d+)$/);
  if (match) return { code: `+${match[1]}`, local: match[2] };
  return { code: '+1', local: cleaned.replace(/^\+/, '') };
};

const getOrCreateWebDeviceId = () => {
  let id = localStorage.getItem('webDeviceId');
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('webDeviceId', id);
  }
  return id;
};

// TrustedDevice.trustedAt is a bare backend LocalDateTime (UTC digits, no
// 'Z') — parse and bucket it in the viewer's own profile-state timezone, not
// the device's.
const formatTrustedDate = (dateStr) => {
  if (!dateStr) return '';
  const hasZone = /[Zz]|[+-]\d{2}:\d{2}$/.test(dateStr);
  const d = new Date(hasZone ? dateStr : `${dateStr}Z`);
  const zone = getViewerTimezone();
  const dayKey = (x) => x.toLocaleDateString('en-CA', { timeZone: zone });
  const todayKey = dayKey(new Date());
  if (dayKey(d) === todayKey) return 'Trusted today';
  if (dayKey(new Date(Date.now() - 86400000)) === dayKey(d)) return 'Trusted yesterday';
  return `Trusted ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: zone })}`;
};

// Web phone-auth needs a domain registered (and reCAPTCHA provisioned) in the Firebase
// Console — a setup step mobile doesn't need (it uses Play Integrity / APNs instead).
// Any of these mean Firebase's client-side verification can't complete in this browser,
// so we should fall back to the backend-only OTP flow rather than surface a hard error.
const FIREBASE_PHONE_AUTH_FALLBACK_CODES = new Set([
  'auth/invalid-app-credential',
  'auth/missing-app-credential',
  'auth/captcha-check-failed',
  'auth/missing-recaptcha-token',
  'auth/argument-error',
  'auth/network-request-failed',
  'auth/internal-error',
  'auth/recaptcha-timeout',
]);

const shouldFallBackToBackendOtp = (err) =>
  FIREBASE_PHONE_AUTH_FALLBACK_CODES.has(err?.code) ||
  /invalid_app_credential|recaptcha|captcha/i.test(err?.message || '');

const getWebDeviceName = () => {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return 'Edge Browser';
  if (/OPR\/|Opera\//.test(ua)) return 'Opera Browser';
  if (/Chrome\//.test(ua)) return 'Chrome Browser';
  if (/Firefox\//.test(ua)) return 'Firefox Browser';
  if (/Safari\//.test(ua)) return 'Safari Browser';
  return 'Web Browser';
};

// Backend field name for the phone number isn't consistent across the app's pages
// (MFAEnrollmentPage sends `phone`, this page historically sent `phoneNumber`) — send
// both so the request works regardless of which key the backend actually reads.
const phoneSendPayload = (formattedPhone) => ({ phone: formattedPhone, phoneNumber: formattedPhone });

// Some Firebase projects fail to provision reCAPTCHA Enterprise for a given domain
// (the SDK logs "Failed to initialize reCAPTCHA Enterprise config" and silently falls
// back to a classic reCAPTCHA v2 "select all images" challenge). That challenge can
// take a real person a while to read, load, and click through — often more than one
// round for numbers/IPs that score as higher-risk (which non-US numbers commonly do).
// It's worth the wait: this Firebase path is the ONLY one that sends the SMS the same
// way the mobile app does (mobile never needs this challenge — the native SDK has no
// RecaptchaVerifier). The "backend" fallback below exists purely so the button doesn't
// hang forever if the challenge truly never appears/resolves — but its OTP-send call
// goes through the backend's own SMS path, not Firebase's, so it isn't a substitute for
// letting this complete. Give it a generous window before giving up.
const RECAPTCHA_VERIFY_TIMEOUT_MS = 120000;

const verifyPhoneNumberWithTimeout = (phoneAuthProvider, options, verifier) => Promise.race([
  phoneAuthProvider.verifyPhoneNumber(options, verifier),
  new Promise((_, reject) => setTimeout(
    () => reject(Object.assign(new Error('reCAPTCHA verification timed out'), { code: 'auth/recaptcha-timeout' })),
    RECAPTCHA_VERIFY_TIMEOUT_MS,
  )),
]);

// The reCAPTCHA v2 challenge ("Select all images with...") renders its own modal
// as a <div> appended directly to document.body — a sibling of #root, outside both
// React's tree and the verifier's #recaptcha-container — so RecaptchaVerifier.clear()
// never removes it. If we time out and fall back while it's showing, it's left
// floating on top of the OTP entry view with nothing to dismiss it. Sweep it out
// manually by locating the body-level wrapper that hosts its bframe iframe.
const removeStrayRecaptchaChallenge = () => {
  document.querySelectorAll('body > div').forEach((el) => {
    if (el.id !== 'root' && el.querySelector('iframe[src*="recaptcha/api2/bframe"]')) {
      el.remove();
    }
  });
};

// ── Component ─────────────────────────────────────────────────────────────────

const SettingsPage = () => {
  const navigate = useNavigate();

  // ── Security status ──
  const [securityStatus, setSecurityStatus] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [emailSent, setEmailSent] = useState(false);

  // ── View state ──  'main' | 'phone-input' | 'phone-otp' | 'mfa-method-select' | 'trust-prompt'
  const [view, setView] = useState('main');

  // ── Phone flow ──
  const [phoneInput, setPhoneInput] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [phoneReadOnly, setPhoneReadOnly] = useState(false);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(0);
  const [firebaseVerificationId, setFirebaseVerificationId] = useState(null);
  const [verificationMode, setVerificationMode] = useState(null); // null | 'firebase' | 'backend'
  const otpRefs = useRef([]);
  const recaptchaVerifierRef = useRef(null);

  // ── Trusted devices ──
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [currentDeviceId, setCurrentDeviceId] = useState(null);
  const [showRevokeAllConfirm, setShowRevokeAllConfirm] = useState(false);

  // ── Shared ──
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [actionLoading, setActionLoading] = useState('');
  const [showMfaDisableConfirm, setShowMfaDisableConfirm] = useState(false);

  // Re-auth prompt (shown when Firebase MFA disable needs a recent sign-in)
  const [reauthVisible, setReauthVisible] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');
  const [reauthError, setReauthError] = useState('');
  const [reauthSubmitting, setReauthSubmitting] = useState(false);
  const pendingUnenrollRef = useRef(null);

  // ── Fingerprint sign-in (WebAuthn passkey) ──
  const passkeySupported = isWebAuthnSupported();
  // True enrollment state comes from the server (see effect below), not a local flag.
  const [passkeyHere, setPasskeyHere] = useState(false);

  // ── Token helper — waits for Firebase auth state on page refresh ──
  const getToken = useCallback(() => new Promise((resolve, reject) => {
    if (auth.currentUser) {
      auth.currentUser.getIdToken(true).then(resolve).catch(reject);
      return;
    }
    const timeoutId = setTimeout(() => {
      unsubscribe();
      navigate('/login');
      resolve(null);
    }, 10000);
    const unsubscribe = onAuthStateChanged(auth, user => {
      clearTimeout(timeoutId);
      unsubscribe();
      if (user) {
        user.getIdToken(true).then(resolve).catch(reject);
      } else {
        navigate('/login');
        resolve(null);
      }
    });
  }), [navigate]);

  // ── Reflect the true passkey enrollment state from the server (not a local flag) ──
  useEffect(() => {
    if (!passkeySupported) return;
    listPasskeys(getToken)
      .then((list) => setPasskeyHere(Array.isArray(list) && list.length > 0))
      .catch(() => { /* non-blocking: leave as not-enrolled on error */ });
  }, [passkeySupported, getToken]);

  // ── Resend countdown ──
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setInterval(() => setResendTimer(p => p - 1), 1000);
    return () => clearInterval(t);
  }, [resendTimer]);

  // ── Cleanup RecaptchaVerifier on unmount ──
  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  // ── Auth guard ──
  useEffect(() => {
    if (localStorage.getItem('isAuthenticated') !== 'true') {
      navigate('/login');
    }
  }, [navigate]);

  // ── Fetch security status (Firebase reload + backend merge) ──
  const fetchSecurityStatus = useCallback(async () => {
    try {
      setPageLoading(true);
      setMsg({ type: '', text: '' });

      // Wait for auth state to be confirmed before reloading user
      const token = await getToken();
      if (!token) return;

      // Reload Firebase user to get fresh emailVerified (auth.currentUser is now set)
      const firebaseUser = auth.currentUser;
      if (firebaseUser) {
        try { await firebaseUser.reload(); } catch (e) { /* non-fatal */ }
      }
      const firebaseEmailVerified = auth.currentUser?.emailVerified ?? false;

      const res = await apiClient.get(API_ENDPOINTS.securityStatus, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const backendData = res.data;

      // Firebase is source of truth for email verification
      const merged = {
        ...backendData,
        emailVerified: firebaseEmailVerified || !!backendData.emailVerified,
        email: auth.currentUser?.email || backendData.email,
      };

      // Sync backend if Firebase says verified but backend doesn't
      if (firebaseEmailVerified && !backendData.emailVerified) {
        try {
          await apiClient.post(API_ENDPOINTS.verifyEmailConfirm, {}, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
        } catch (e) { /* non-blocking */ }
      }

      // Same self-heal for the phone: a number enrolled as a Firebase MFA factor
      // was proven by SMS OTP, so it IS verified — the backend flag can lag behind
      // when the post-enrollment sync didn't land (older builds of this page fired
      // confirm + mfaEnable concurrently and the two could overwrite each other).
      // Only phoneVerified is healed, and only when the enrolled number matches the
      // backend's saved number (confirm marks the *saved* number verified).
      // mfaEnabled is deliberately NOT touched: a factor can linger in Firebase after
      // a Disable whose Firebase unenroll failed, and that must not re-enable MFA.
      if (!backendData.phoneVerified && backendData.maskedPhone) {
        const enrolledMatch = getEnrolledMfaPhones(auth.currentUser)
          .some(p => maskPhoneLikeBackend(p) === backendData.maskedPhone);
        if (enrolledMatch) {
          try {
            await apiClient.post(API_ENDPOINTS.verifyPhoneConfirm, { verified: true }, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            merged.phoneVerified = true;
          } catch (e) { /* non-blocking — retried on next load */ }
        }
      }
      setSecurityStatus(merged);
    } catch (err) {
      console.error('fetchSecurityStatus error:', err);
      setMsg({ type: 'error', text: 'Failed to load security settings. Please try again.' });
    } finally {
      setPageLoading(false);
    }
  }, [getToken]);

  // ── Fetch trusted devices ──
  const fetchTrustedDevices = useCallback(async () => {
    const deviceId = getOrCreateWebDeviceId();
    setCurrentDeviceId(deviceId);
    setDevicesLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      const res = await apiClient.get(API_ENDPOINTS.trustedDevices, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const raw = Array.isArray(res.data?.devices) ? res.data.devices
               : Array.isArray(res.data) ? res.data : [];
      setTrustedDevices(raw.map(d => ({ ...d, isCurrent: d.deviceId === deviceId })));
    } catch (err) {
      // Non-critical — trusted devices section degrades gracefully
    } finally {
      setDevicesLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchSecurityStatus();
    fetchTrustedDevices();
  }, [fetchSecurityStatus, fetchTrustedDevices]);

  // ── Email verification ──
  const handleSendEmailVerification = async () => {
    try {
      setActionLoading('send-email');
      setMsg({ type: '', text: '' });
      const token = await getToken();
      if (!token) return;
      await apiClient.post(API_ENDPOINTS.verifyEmailSend, {}, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setEmailSent(true);
    } catch (err) {
      console.error('sendEmailVerification error:', err);
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to send verification email. Please try again.' });
    } finally {
      setActionLoading('');
    }
  };

  // ── Navigate to phone-input, prefill from status ──
  const goToPhoneInput = useCallback(() => {
    if (securityStatus?.phone) {
      const { code, local } = parsePhone(securityStatus.phone);
      const country = COUNTRIES.find(c => c.code === code) || COUNTRIES[0];
      setSelectedCountry(country);
      setPhoneInput(local);
      setPhoneReadOnly(true);
    } else {
      setPhoneInput('');
      setSelectedCountry(COUNTRIES[0]);
      setPhoneReadOnly(false);
    }
    setMsg({ type: '', text: '' });
    setView('phone-input');
  }, [securityStatus]);

  // ── Phone already enrolled as a Firebase MFA factor ──
  // Re-running Firebase enrollment for it fails with auth/second-factor-already-in-use,
  // but that enrollment already proved ownership by SMS OTP — so mark the phone
  // verified on the backend instead of sending a new code. verifyPhoneSend (which
  // saves the number AND resets phoneVerified=false) is only called when the backend
  // holds a different number, and is awaited before confirm so it can't overwrite it.
  // mfaEnabled is left to the explicit Enable MFA action (see fetchSecurityStatus).
  const markEnrolledPhoneVerified = async (formattedPhone) => {
    const token = await getToken();
    if (!token) return;
    const headers = { headers: { 'Authorization': `Bearer ${token}` } };
    if (maskPhoneLikeBackend(formattedPhone) !== securityStatus?.maskedPhone) {
      await apiClient.post(API_ENDPOINTS.verifyPhoneSend, phoneSendPayload(formattedPhone), headers);
    }
    await apiClient.post(API_ENDPOINTS.verifyPhoneConfirm, { verified: true }, headers);

    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
    removeStrayRecaptchaChallenge();
    setFirebaseVerificationId(null);
    setVerificationMode(null);
    setPhoneInput('');
    setOtpDigits(['', '', '', '', '', '']);
    setView('main');
    await fetchSecurityStatus();
    // After the refresh — fetchSecurityStatus clears the status message on start.
    setMsg({ type: 'success', text: 'This number is already verified for multi-factor authentication. Phone marked as verified.' });
  };

  // ── Phone OTP ──
  const handleSendPhoneOtp = async () => {
    const digits = phoneInput.replace(/\D/g, '');
    const minLen = selectedCountry.code === '+1' ? 10 : 7;
    if (digits.length < minLen) {
      setMsg({ type: 'error', text: `Please enter a valid phone number for ${selectedCountry.name}.` });
      return;
    }
    try {
      setActionLoading('send-otp');
      setMsg({ type: '', text: '' });

      const user = auth.currentUser;
      if (!user) { navigate('/login'); return; }

      // Firebase requires a verified email before MFA phone enrollment
      await user.reload();
      if (!auth.currentUser?.emailVerified) {
        setMsg({ type: 'error', text: 'Please verify your email address before adding phone verification.' });
        return;
      }

      const formattedPhone = `${selectedCountry.code}${digits}`;

      // Already enrolled (user.reload() above refreshed the factor list) — no SMS needed.
      if (getEnrolledMfaPhones(auth.currentUser).includes(normalizePhone(formattedPhone))) {
        await markEnrolledPhoneVerified(formattedPhone);
        return;
      }

      try {
        // Primary: Firebase MFA flow (matches mobile app — Firebase sends the SMS)
        const multiFactorUser = multiFactor(auth.currentUser);
        const session = await multiFactorUser.getSession();

        if (recaptchaVerifierRef.current) {
          recaptchaVerifierRef.current.clear();
          recaptchaVerifierRef.current = null;
        }
        recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });

        // This can silently render a "Confirm you're not a robot" challenge on top of
        // the page — let the user know to look for it so they don't mistake the wait
        // for the button being stuck (it's what makes Firebase actually send the SMS).
        setMsg({ type: 'info', text: 'Sending your code — if a verification challenge appears on screen, please complete it to continue.' });

        const phoneAuthProvider = new PhoneAuthProvider(auth);
        const verificationId = await verifyPhoneNumberWithTimeout(
          phoneAuthProvider,
          { phoneNumber: formattedPhone, session },
          recaptchaVerifierRef.current,
        );
        setFirebaseVerificationId(verificationId);
        setVerificationMode('firebase');

        // Non-blocking backend notify
        const token = await getToken();
        if (token) {
          apiClient.post(
            API_ENDPOINTS.verifyPhoneSend,
            phoneSendPayload(formattedPhone),
            { headers: { 'Authorization': `Bearer ${token}` } },
          ).catch(e => console.warn('[Phone OTP] Backend notify failed (non-blocking):', e));
        }
      } catch (firebaseErr) {
        // Safety net for the pre-check above (e.g. a stale factor list).
        if (firebaseErr?.code === 'auth/second-factor-already-in-use') {
          await markEnrolledPhoneVerified(formattedPhone);
          return;
        }
        // Web-only Firebase setup issue (unauthorized domain, reCAPTCHA blocked, etc.) —
        // fall back to backend-only OTP flow (works on all domains without Console setup).
        if (!shouldFallBackToBackendOtp(firebaseErr)) throw firebaseErr; // rethrow unrelated Firebase errors

        // The abandoned verifyPhoneNumber() call may have left its reCAPTCHA v2
        // challenge ("select all images" overlay) rendered on top of the page —
        // tear down the verifier and sweep the stray overlay before showing the
        // backend OTP entry view underneath it.
        if (recaptchaVerifierRef.current) {
          recaptchaVerifierRef.current.clear();
          recaptchaVerifierRef.current = null;
        }
        removeStrayRecaptchaChallenge();

        console.warn('[Phone OTP] Firebase web verification unavailable, using backend fallback:', firebaseErr.code || firebaseErr.message);
        const token = await getToken();
        if (!token) return;
        await apiClient.post(
          API_ENDPOINTS.verifyPhoneSend,
          phoneSendPayload(formattedPhone),
          { headers: { 'Authorization': `Bearer ${token}` } },
        );
        setFirebaseVerificationId(null);
        setVerificationMode('backend');
      }

      setMsg({ type: '', text: '' });
      setOtpDigits(['', '', '', '', '', '']);
      setView('phone-otp');
      setResendTimer(RESEND_TIMER_SECONDS);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      console.error('sendPhoneOtp error:', err);
      if (err.code === 'auth/requires-recent-login') {
        setMsg({ type: 'error', text: 'Your session has expired. Please log out and log back in, then try again.' });
      } else if (err.code === 'auth/unverified-email') {
        setMsg({ type: 'error', text: 'Please verify your email before adding phone verification.' });
      } else {
        setMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to send verification code. Please try again.' });
      }
    } finally {
      setActionLoading('');
    }
  };

  const handleVerifyPhoneOtp = async () => {
    const code = otpDigits.join('');
    if (code.length < OTP_LENGTH) {
      setMsg({ type: 'error', text: 'Please enter all 6 digits of the verification code.' });
      return;
    }
    if (!verificationMode) {
      setMsg({ type: 'error', text: 'Verification session expired. Please resend the code.' });
      return;
    }
    let backendSyncFailed = false;
    try {
      setActionLoading('verify-otp');
      setMsg({ type: '', text: '' });

      if (verificationMode === 'firebase') {
        // Firebase MFA enrollment — matches mobile app flow exactly
        const credential = PhoneAuthProvider.credential(firebaseVerificationId, code);
        const assertion = PhoneMultiFactorGenerator.assertion(credential);
        const user = auth.currentUser;
        if (!user) { navigate('/login'); return; }
        await multiFactor(user).enroll(assertion, 'Phone Number');

        // Backend sync — sequential, same order as mobile (SecuritySettingsScreen):
        // mfaEnable rejects unless phoneVerified is already true, and both endpoints
        // save the whole user doc, so running them concurrently could fail the enable
        // or overwrite phoneVerified back to false. Firebase enrollment above is the
        // real success: a sync failure is logged + surfaced as a notice, never thrown
        // (retrying would only hit auth/second-factor-already-in-use), and
        // fetchSecurityStatus self-heals phoneVerified on the next load.
        const token = await getToken();
        if (token) {
          const headers = { headers: { 'Authorization': `Bearer ${token}` } };
          try {
            await apiClient.post(API_ENDPOINTS.verifyPhoneConfirm, { code }, headers);
            try {
              await apiClient.post(API_ENDPOINTS.mfaEnable, { mfaMethod: 'phone' }, headers);
            } catch (e) {
              console.warn('[Phone OTP] Backend mfaEnable failed (non-blocking):', e);
              backendSyncFailed = true;
            }
          } catch (e) {
            console.warn('[Phone OTP] Backend confirmPhone failed (non-blocking):', e);
            backendSyncFailed = true;
          }
        } else {
          backendSyncFailed = true;
        }
      } else {
        // Backend-only mode (fallback when Firebase phone auth not configured for domain)
        const token = await getToken();
        if (!token) return;
        await apiClient.post(
          API_ENDPOINTS.verifyPhoneConfirm,
          { code },
          { headers: { 'Authorization': `Bearer ${token}` } },
        );
        try {
          await apiClient.post(
            API_ENDPOINTS.mfaEnable,
            { mfaMethod: 'phone' },
            { headers: { 'Authorization': `Bearer ${token}` } },
          );
        } catch (e) { /* non-blocking */ }
      }

      setFirebaseVerificationId(null);
      setVerificationMode(null);
      setPhoneInput('');
      setOtpDigits(['', '', '', '', '', '']);
      const isCurrentDeviceTrusted = trustedDevices.some(d => (d.deviceId || d.id) === currentDeviceId);
      if (!isCurrentDeviceTrusted) {
        setMsg({ type: '', text: '' });
        setView('trust-prompt');
      } else {
        setView('main');
      }
      await fetchSecurityStatus();
      // Set after the refresh — fetchSecurityStatus clears the status message on start.
      if (isCurrentDeviceTrusted) {
        setMsg(backendSyncFailed
          ? { type: 'info', text: 'Phone enrolled for multi-factor authentication. Some account settings are still syncing — refresh this page in a moment.' }
          : { type: 'success', text: 'Phone verified & MFA enrolled successfully.' });
      }
    } catch (err) {
      console.error('verifyPhoneOtp error:', err);
      setMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Invalid verification code. Please try again.' });
    } finally {
      setActionLoading('');
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    try {
      setActionLoading('resend-otp');

      const user = auth.currentUser;
      if (!user) { navigate('/login'); return; }

      const formattedPhone = `${selectedCountry.code}${phoneInput.replace(/\D/g, '')}`;

      try {
        // Primary: Firebase MFA resend
        const multiFactorUser = multiFactor(user);
        const session = await multiFactorUser.getSession();

        if (recaptchaVerifierRef.current) {
          recaptchaVerifierRef.current.clear();
          recaptchaVerifierRef.current = null;
        }
        recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });

        setMsg({ type: 'info', text: 'Resending your code — if a verification challenge appears on screen, please complete it to continue.' });

        const phoneAuthProvider = new PhoneAuthProvider(auth);
        const verificationId = await verifyPhoneNumberWithTimeout(
          phoneAuthProvider,
          { phoneNumber: formattedPhone, session },
          recaptchaVerifierRef.current,
        );
        setFirebaseVerificationId(verificationId);
        setVerificationMode('firebase');

        // Non-blocking backend notify
        const token = await getToken();
        if (token) {
          apiClient.post(
            API_ENDPOINTS.verifyPhoneSend,
            phoneSendPayload(formattedPhone),
            { headers: { 'Authorization': `Bearer ${token}` } },
          ).catch(e => console.warn('[Phone OTP] Backend resend notify failed (non-blocking):', e));
        }
      } catch (firebaseErr) {
        if (firebaseErr?.code === 'auth/second-factor-already-in-use') {
          await markEnrolledPhoneVerified(formattedPhone);
          return;
        }
        if (!shouldFallBackToBackendOtp(firebaseErr)) throw firebaseErr;

        // Same teardown as the send flow — remove the stuck reCAPTCHA v2 challenge
        // before the OTP entry view takes over.
        if (recaptchaVerifierRef.current) {
          recaptchaVerifierRef.current.clear();
          recaptchaVerifierRef.current = null;
        }
        removeStrayRecaptchaChallenge();

        console.warn('[Phone OTP] Firebase web verification unavailable on resend, using backend fallback:', firebaseErr.code || firebaseErr.message);
        const token = await getToken();
        if (!token) return;
        await apiClient.post(
          API_ENDPOINTS.verifyPhoneSend,
          phoneSendPayload(formattedPhone),
          { headers: { 'Authorization': `Bearer ${token}` } },
        );
        setFirebaseVerificationId(null);
        setVerificationMode('backend');
      }

      setOtpDigits(['', '', '', '', '', '']);
      setResendTimer(RESEND_TIMER_SECONDS);
      setMsg({ type: 'info', text: 'A new verification code has been sent.' });
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to resend code. Please try again.' });
    } finally {
      setActionLoading('');
    }
  };

  // ── MFA ──
  const handleEnableMfa = async () => {
    try {
      setActionLoading('enable-mfa');
      setMsg({ type: '', text: '' });

      // Check Firebase MFA enrollment status (matches mobile: enrolledFactors check)
      const user = auth.currentUser;
      const enrolledFactors = user ? multiFactor(user).enrolledFactors : [];
      const isFirebaseEnrolled = enrolledFactors.length > 0;

      if (!isFirebaseEnrolled) {
        // Not enrolled in Firebase — redirect to phone verification which handles
        // Firebase enrollment + backend sync (same path as mobile app)
        setActionLoading('');
        goToPhoneInput();
        return;
      }

      // Already enrolled in Firebase — just sync enable state with backend
      const token = await getToken();
      if (!token) return;
      await apiClient.post(
        API_ENDPOINTS.mfaEnable,
        { mfaMethod: 'phone' },
        { headers: { 'Authorization': `Bearer ${token}` } },
      );
      setMsg({ type: 'success', text: 'Multi-factor authentication enabled successfully.' });
      setView('main');
      await fetchSecurityStatus();
    } catch (err) {
      console.error('enableMfa error:', err);
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to enable MFA. Please try again.' });
    } finally {
      setActionLoading('');
    }
  };

  const callBackendDisable = async () => {
    const token = await getToken();
    if (!token) return;
    await apiClient.post(
      API_ENDPOINTS.mfaDisable,
      {},
      { headers: { 'Authorization': `Bearer ${token}` } },
    );
    setMsg({ type: 'success', text: 'Multi-factor authentication has been disabled.' });
    await fetchSecurityStatus();
  };

  const handleDisableMfa = async () => {
    setShowMfaDisableConfirm(false);
    try {
      setActionLoading('disable-mfa');
      setMsg({ type: '', text: '' });

      // Firebase enrollment is the source of truth — if we leave it in place,
      // mobile login still challenges for MFA even after the backend flag is off.
      // Unenroll must succeed (or there must be nothing to unenroll) before we
      // flip the backend flag.
      const user = auth.currentUser;
      const multiFactorUser = user ? multiFactor(user) : null;
      const phoneFactors = multiFactorUser
        ? multiFactorUser.enrolledFactors.filter(f => f.factorId === 'phone')
        : [];

      if (phoneFactors.length > 0) {
        try {
          for (const factor of phoneFactors) {
            await multiFactorUser.unenroll(factor);
          }
        } catch (firebaseErr) {
          if (firebaseErr.code === 'auth/requires-recent-login' && user?.email) {
            pendingUnenrollRef.current = { user, multiFactorUser, phoneFactors };
            setReauthPassword('');
            setReauthError('');
            setReauthVisible(true);
            return;
          }
          console.error('[MFA Disable] Firebase unenroll failed:', firebaseErr);
          setMsg({ type: 'error', text: firebaseErr.message || 'Failed to disable MFA in Firebase. Please try again.' });
          return;
        }
      }

      await callBackendDisable();
    } catch (err) {
      console.error('disableMfa error:', err);
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to disable MFA. Please try again.' });
    } finally {
      setActionLoading('');
    }
  };

  const handleReauthCancel = () => {
    setReauthVisible(false);
    setReauthPassword('');
    setReauthError('');
    pendingUnenrollRef.current = null;
  };

  const handleReauthSubmit = async () => {
    const password = reauthPassword.trim();
    if (!password) {
      setReauthError('Password is required.');
      return;
    }
    const pending = pendingUnenrollRef.current;
    if (!pending?.user?.email) {
      setReauthError('Session expired. Please close and try again.');
      return;
    }
    setReauthSubmitting(true);
    setReauthError('');
    try {
      await reauthenticateWithCredential(
        pending.user,
        EmailAuthProvider.credential(pending.user.email, password),
      );
      for (const factor of pending.phoneFactors) {
        await pending.multiFactorUser.unenroll(factor);
      }
      await callBackendDisable();
      setReauthVisible(false);
      setReauthPassword('');
      pendingUnenrollRef.current = null;
    } catch (err) {
      console.error('[MFA Disable] Re-auth or retry failed:', err);
      setReauthError(
        err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential'
          ? 'Incorrect password. Please try again.'
          : err.message || 'Re-authentication failed. Please try again.',
      );
    } finally {
      setReauthSubmitting(false);
    }
  };

  // ── Trusted devices ──
  const handleRevokeDevice = async (device) => {
    const deviceId = device.deviceId || device.id;
    const deviceName = device.deviceName || 'Unknown device';
    // Context-aware confirmation message — matches mobile app exactly
    const label = device.isCurrent ? `this device (${deviceName})` : deviceName;
    const suffix = device.isCurrent
      ? "You'll need to verify with OTP on your next login."
      : "You'll need to verify with OTP on next login from that device.";
    if (!window.confirm(`Remove ${label} from trusted devices? ${suffix}`)) return;
    try {
      setActionLoading('revoke-device');
      setMsg({ type: '', text: '' });
      const token = await getToken();
      if (!token) return;
      await apiClient.delete(API_ENDPOINTS.trustedDeviceRevoke(deviceId), {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setTrustedDevices(prev => prev.filter(d => (d.deviceId || d.id) !== deviceId));
      if (device.isCurrent) localStorage.removeItem('webDeviceTrusted');
      setMsg({ type: 'success', text: 'Device removed successfully.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to revoke device. Please try again.' });
    } finally {
      setActionLoading('');
    }
  };

  const handleRevokeAllDevices = async () => {
    setShowRevokeAllConfirm(false);
    try {
      setActionLoading('revoke-all');
      setMsg({ type: '', text: '' });
      const token = await getToken();
      if (!token) return;
      await apiClient.delete(API_ENDPOINTS.trustedDevices, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setTrustedDevices([]);
      setMsg({ type: 'success', text: 'All trusted devices have been revoked.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to revoke all devices. Please try again.' });
    } finally {
      setActionLoading('');
    }
  };

  // ── Set up fingerprint sign-in on this browser (WebAuthn passkey enrollment) ──
  const handleEnrollPasskey = async () => {
    try {
      setActionLoading('enroll-passkey');
      setMsg({ type: '', text: '' });
      await enrollPasskey(getToken);
      setPasskeyHere(true);
      setMsg({ type: 'success', text: 'Fingerprint sign-in is set up for this browser.' });
    } catch (err) {
      const cancelled = err?.name === 'NotAllowedError' || err?.name === 'AbortError';
      setMsg({
        type: 'error',
        text: cancelled
          ? 'Fingerprint setup was cancelled.'
          : (err.response?.data?.message || 'Could not set up fingerprint sign-in. Please try again.'),
      });
    } finally {
      setActionLoading('');
    }
  };

  // ── Trust current browser ──
  const handleTrustCurrentDevice = async () => {
    try {
      setActionLoading('trust-device');
      setMsg({ type: '', text: '' });
      const token = await getToken();
      if (!token) return;
      await apiClient.post(
        API_ENDPOINTS.trustedDeviceTrust,
        { deviceId: currentDeviceId, deviceName: getWebDeviceName(), platform: 'web' },
        { headers: { 'Authorization': `Bearer ${token}` } },
      );
      localStorage.setItem('webDeviceTrusted', 'true');
      setMsg({ type: 'success', text: 'This browser is now trusted. Future logins will skip OTP verification.' });
      setView('main');
      await fetchTrustedDevices();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to trust this browser. Please try again.' });
      setView('main');
    } finally {
      setActionLoading('');
    }
  };

  const handleSkipTrust = () => {
    setMsg({ type: 'success', text: 'Phone verified & MFA enrolled successfully.' });
    setView('main');
  };

  // ── OTP input handlers ──
  const handleOtpInput = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, OTP_LENGTH).split('');
      const newOtp = [...otpDigits];
      digits.forEach((d, i) => { if (index + i < OTP_LENGTH) newOtp[index + i] = d; });
      setOtpDigits(newOtp);
      setTimeout(() => otpRefs.current[Math.min(index + digits.length, OTP_LENGTH - 1)]?.focus(), 0);
      if (newOtp.join('').length === OTP_LENGTH) setTimeout(handleVerifyPhoneOtp, 0);
      return;
    }
    const digit = value.replace(/\D/g, '').slice(-1);
    const newOtp = [...otpDigits];
    newOtp[index] = digit;
    setOtpDigits(newOtp);
    if (digit && index < OTP_LENGTH - 1) setTimeout(() => otpRefs.current[index + 1]?.focus(), 0);
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      setTimeout(() => otpRefs.current[index - 1]?.focus(), 0);
    }
  };

  // ── Status message ──
  const StatusMessage = () => {
    if (!msg.text) return null;
    return (
      <div className={`ssp-msg ssp-msg--${msg.type}`}>
        {msg.type === 'success' && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        )}
        {msg.type === 'error' && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        )}
        {msg.type === 'warning' && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        )}
        {msg.type === 'info' && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        )}
        <span>{msg.text}</span>
      </div>
    );
  };

  if (pageLoading) {
    return (
      <div className="ssp-page">
        <main className="ssp-main">
          <div className="ssp-container">
            <p className="ssp-loading">Loading security settings...</p>
          </div>
        </main>
      </div>
    );
  }

  // ── Derived values ──
  const canTrustCurrentDevice = !!securityStatus?.mfaEnabled
    && !devicesLoading
    && !!currentDeviceId
    && !trustedDevices.some(d => (d.deviceId || d.id) === currentDeviceId);

  const mfaSubtitle = securityStatus?.mfaEnabled
    ? `Enabled (${securityStatus.mfaMethod === 'phone' ? 'Phone OTP' : 'Email OTP'})`
    : 'Disabled';

  // security-status returns the number only as `maskedPhone` (already masked
  // server-side, e.g. "+1***1234") — never a raw `phone` — so reading only
  // `phone` hid the Verified badge and showed "Not added" for every user.
  // Mirrors mobile security.service (`data.maskedPhone || data.phone`).
  // Display-only: goToPhoneInput still keys off the raw `phone`, so the
  // phone-input step's prefill/read-only behavior is unchanged.
  const displayStatusPhone = securityStatus?.maskedPhone
    || (securityStatus?.phone ? maskPhone(securityStatus.phone) : '');

  const phoneActionLabel = securityStatus?.phoneVerified
    ? 'Change phone number'
    : displayStatusPhone ? 'Verify Phone' : 'Add & Verify Phone';

  const displayPhone = `${selectedCountry.code}${phoneInput.replace(/\D/g, '')}`;

  return (
    <div className="ssp-page">
      <main className="ssp-main">
        <div className="ssp-container">

          <div className="ssp-header">
            <button className="ssp-back-btn" onClick={() => navigate(-1)}>
              <span>←</span>
              <span>Back</span>
            </button>
            <h1 className="ssp-title">Security & Settings</h1>
            <p className="ssp-subtitle">Manage your account security and verification.</p>
          </div>

          <StatusMessage />

          {/* ══ Main view ══ */}
          {view === 'main' && (
            <div className="ssp-content">

              {/* Email Verification */}
              <div className="ssp-section">
                <p className="ssp-section-label">Email Verification</p>
                <div className="ssp-card">
                  <div className="ssp-card-row">
                    <div className="ssp-card-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </div>
                    <div className="ssp-card-info">
                      <span className="ssp-card-title">Email</span>
                      <span className="ssp-card-subtitle">{maskEmail(securityStatus?.email) || '—'}</span>
                    </div>
                    <span className={`ssp-badge ${securityStatus?.emailVerified ? 'ssp-badge--verified' : 'ssp-badge--unverified'}`}>
                      {securityStatus?.emailVerified ? 'Verified' : 'Unverified'}
                    </span>
                  </div>

                  {!securityStatus?.emailVerified && (
                    <>
                      <div className="ssp-card-divider" />
                      {emailSent ? (
                        <div className="ssp-card-row ssp-email-sent-row">
                          <div className="ssp-email-sent-msg">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a8c5b" strokeWidth="2">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                              <polyline points="22,6 12,13 2,6"/>
                            </svg>
                            <span>Check your inbox</span>
                          </div>
                          <button
                            className="ssp-resend-btn"
                            onClick={handleSendEmailVerification}
                            disabled={actionLoading === 'send-email'}
                          >
                            Resend
                          </button>
                        </div>
                      ) : (
                        <button
                          className="ssp-card-action"
                          onClick={handleSendEmailVerification}
                          disabled={actionLoading === 'send-email'}
                        >
                          <span>{actionLoading === 'send-email' ? 'Sending...' : 'Send Verification Email'}</span>
                          {actionLoading !== 'send-email' && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polyline points="9 18 15 12 9 6"/>
                            </svg>
                          )}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Phone Verification */}
              <div className="ssp-section">
                <p className="ssp-section-label">Phone Verification</p>
                <div className="ssp-card">
                  <div className="ssp-card-row">
                    <div className="ssp-card-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                    </div>
                    <div className="ssp-card-info">
                      <span className="ssp-card-title">Phone</span>
                      <span className="ssp-card-subtitle">
                        {displayStatusPhone || 'Not added'}
                      </span>
                    </div>
                    {displayStatusPhone && (
                      <span className={`ssp-badge ${securityStatus?.phoneVerified ? 'ssp-badge--verified' : 'ssp-badge--unverified'}`}>
                        {securityStatus?.phoneVerified ? 'Verified' : 'Unverified'}
                      </span>
                    )}
                  </div>
                  <div className="ssp-card-divider" />
                  <button className="ssp-card-action" onClick={goToPhoneInput}>
                    <span>{phoneActionLabel}</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Multi-Factor Authentication */}
              <div className="ssp-section">
                <p className="ssp-section-label">Multi-Factor Authentication</p>
                <div className="ssp-card">
                  <div className="ssp-card-row">
                    <div className="ssp-card-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                    </div>
                    <div className="ssp-card-info">
                      <span className="ssp-card-title">MFA</span>
                      <span className="ssp-card-subtitle">{mfaSubtitle}</span>
                    </div>
                    <span className={`ssp-badge ${securityStatus?.mfaEnabled ? 'ssp-badge--verified' : 'ssp-badge--disabled'}`}>
                      {securityStatus?.mfaEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="ssp-card-divider" />
                  {securityStatus?.mfaEnabled ? (
                    showMfaDisableConfirm ? (
                      <div className="ssp-mfa-confirm">
                        <p>Disabling MFA will make your account less secure. Are you sure?</p>
                        <div className="ssp-mfa-confirm-btns">
                          <button onClick={() => setShowMfaDisableConfirm(false)}>Cancel</button>
                          <button
                            className="destructive"
                            onClick={handleDisableMfa}
                            disabled={actionLoading === 'disable-mfa'}
                          >
                            {actionLoading === 'disable-mfa' ? 'Disabling...' : 'Yes, Disable'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button className="ssp-card-action ssp-card-action--danger" onClick={() => setShowMfaDisableConfirm(true)}>
                        <span>Disable MFA</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </button>
                    )
                  ) : (
                    <button
                      className="ssp-card-action"
                      onClick={() => setView('mfa-method-select')}
                    >
                      <span>Enable MFA</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </button>
                  )}
                </div>
                <p className="ssp-info-note">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  MFA adds an extra layer of security to your account by requiring a verification code at login.
                </p>
              </div>

              {/* Trusted Devices */}
              <div className="ssp-section">
                <p className="ssp-section-label">Trusted Devices</p>
                <div className="ssp-card">
                  {devicesLoading ? (
                    <div className="ssp-card-row ssp-devices-loading">
                      <div className="ssp-spinner" />
                      <span className="ssp-card-subtitle">Loading devices...</span>
                    </div>
                  ) : trustedDevices.length === 0 ? (
                    <>
                      <div className="ssp-card-row">
                        <div className="ssp-card-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                          </svg>
                        </div>
                        <div className="ssp-card-info">
                          <span className="ssp-card-title">No trusted devices</span>
                          <span className="ssp-card-subtitle">
                            {canTrustCurrentDevice
                              ? 'Trust this browser to skip OTP on future logins'
                              : 'Devices trusted after MFA verification will appear here'}
                          </span>
                        </div>
                      </div>
                      {canTrustCurrentDevice && (
                        <>
                          <div className="ssp-card-divider" />
                          <button
                            className="ssp-card-action"
                            onClick={handleTrustCurrentDevice}
                            disabled={actionLoading === 'trust-device'}
                          >
                            <span>{actionLoading === 'trust-device' ? 'Trusting...' : 'Trust This Browser'}</span>
                            {actionLoading !== 'trust-device' && (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                              </svg>
                            )}
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {trustedDevices.map((device, i) => (
                        <React.Fragment key={device.deviceId || device.id}>
                          {i > 0 && <div className="ssp-card-divider" />}
                          <div className="ssp-device-row">
                            <div className="ssp-card-icon">
                              {/ios|iphone|ipad/i.test(device.platform || '') ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
                                </svg>
                              ) : /android/i.test(device.platform || '') ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
                                </svg>
                              ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                                </svg>
                              )}
                            </div>
                            <div className="ssp-card-info">
                              <div className="ssp-device-name-row">
                                <span className="ssp-card-title">{device.deviceName || 'Unknown device'}</span>
                                {device.isCurrent && <span className="ssp-device-current-badge">This device</span>}
                              </div>
                              <span className="ssp-card-subtitle">{formatTrustedDate(device.trustedAt)}</span>
                            </div>
                            <button
                              className="ssp-device-revoke-btn"
                              title="Revoke device"
                              disabled={actionLoading === 'revoke-device'}
                              onClick={() => handleRevokeDevice(device)}
                            >
                              {actionLoading === 'revoke-device' ? (
                                <div className="ssp-spinner" style={{ width: 16, height: 16 }} />
                              ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                  <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                                </svg>
                              )}
                            </button>
                          </div>
                        </React.Fragment>
                      ))}
                      {canTrustCurrentDevice && (
                        <>
                          <div className="ssp-card-divider" />
                          <button
                            className="ssp-card-action"
                            onClick={handleTrustCurrentDevice}
                            disabled={actionLoading === 'trust-device'}
                          >
                            <span>{actionLoading === 'trust-device' ? 'Trusting...' : 'Trust This Browser'}</span>
                            {actionLoading !== 'trust-device' && (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                              </svg>
                            )}
                          </button>
                        </>
                      )}
                      {trustedDevices.length > 1 && (
                        <>
                          <div className="ssp-card-divider" />
                          {showRevokeAllConfirm ? (
                            <div className="ssp-mfa-confirm">
                              <p>Remove all trusted devices? You will need to verify with OTP on next login from any device.</p>
                              <div className="ssp-mfa-confirm-btns">
                                <button onClick={() => setShowRevokeAllConfirm(false)}>Cancel</button>
                                <button
                                  className="destructive"
                                  onClick={handleRevokeAllDevices}
                                  disabled={actionLoading === 'revoke-all'}
                                >
                                  {actionLoading === 'revoke-all' ? 'Revoking...' : 'Revoke All'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button className="ssp-card-action ssp-card-action--danger" onClick={() => setShowRevokeAllConfirm(true)}>
                              <span>Revoke All Devices</span>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="9 18 15 12 9 6"/>
                              </svg>
                            </button>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
                <p className="ssp-info-note">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  Trusted browsers skip OTP verification on future logins. Remove a device at any time to re-require OTP.
                </p>
              </div>

              {/* Fingerprint Sign-in (WebAuthn passkey) */}
              {passkeySupported && (
                <div className="ssp-section">
                  <p className="ssp-section-label">Fingerprint Sign-in</p>
                  <div className="ssp-card">
                    <div className="ssp-card-row">
                      <div className="ssp-card-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 11c-1.66 0-3 1.34-3 3 0 1.53.36 3.06.99 4.4"/>
                          <path d="M12 2a10 10 0 0 0-8.66 15"/>
                          <path d="M7.5 4.2A8 8 0 0 1 20 11c0 1.5-.2 3-.6 4.4"/>
                          <path d="M12 7a4 4 0 0 1 4 4c0 2.5-.3 5-1 7"/>
                        </svg>
                      </div>
                      <div className="ssp-card-info">
                        <span className="ssp-card-title">Sign in with Fingerprint</span>
                        <span className="ssp-card-subtitle">
                          {passkeyHere
                            ? 'Enabled on this browser'
                            : 'Use this device’s fingerprint / Windows Hello to sign in'}
                        </span>
                      </div>
                      {passkeyHere && <span className="ssp-badge ssp-badge--verified">Enabled</span>}
                    </div>
                    {!passkeyHere && (
                      <>
                        <div className="ssp-card-divider" />
                        <button
                          className="ssp-card-action"
                          onClick={handleEnrollPasskey}
                          disabled={actionLoading === 'enroll-passkey'}
                        >
                          <span>{actionLoading === 'enroll-passkey' ? 'Setting up…' : 'Set Up Fingerprint Sign-in'}</span>
                          {actionLoading !== 'enroll-passkey' && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polyline points="9 18 15 12 9 6"/>
                            </svg>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                  <p className="ssp-info-note">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                    Fingerprint sign-in is set up per browser/device. Set it up again on each computer or phone browser you use.
                  </p>
                </div>
              )}

              <button className="ssp-refresh-btn" onClick={() => { fetchSecurityStatus(); fetchTrustedDevices(); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
                Refresh
              </button>
            </div>
          )}

          {/* ══ MFA method selection view ══ */}
          {view === 'mfa-method-select' && (
            <div className="ssp-verify-flow">
              <button className="ssp-back-btn" onClick={() => { setView('main'); setMsg({ type: '', text: '' }); }}>
                <span>←</span><span>Back</span>
              </button>
              <h2 className="ssp-verify-title">Enable MFA</h2>
              <p className="ssp-verify-subtitle">
                MFA adds an extra verification step when you log in using your phone number.
              </p>

              <div className="ssp-method-card ssp-method-card--selected">
                <div className="ssp-method-header">
                  <div className="ssp-card-icon" style={{ width: 36, height: 36 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </div>
                  <span className="ssp-method-title">Phone OTP</span>
                </div>
                <p className="ssp-method-desc">
                  {displayStatusPhone
                    ? `Receive a verification code at ${displayStatusPhone}`
                    : 'Add and verify a phone number first'}
                </p>
                {!securityStatus?.phoneVerified && (
                  <div className="ssp-method-warning">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    <span>
                      Please verify your phone number first —{' '}
                      <button className="ssp-inline-link" onClick={goToPhoneInput}>verify now</button>
                    </span>
                  </div>
                )}
              </div>

              <button
                className="ssp-btn-primary"
                onClick={handleEnableMfa}
                disabled={actionLoading === 'enable-mfa' || !securityStatus?.phoneVerified}
              >
                {actionLoading === 'enable-mfa' ? 'Enabling...' : 'Enable MFA'}
              </button>
            </div>
          )}

          {/* ══ Phone input view ══ */}
          {view === 'phone-input' && (
            <div className="ssp-verify-flow">
              <button className="ssp-back-btn" onClick={() => { setView('main'); setMsg({ type: '', text: '' }); }}>
                <span>←</span><span>Back</span>
              </button>
              <h2 className="ssp-verify-title">Verify Phone Number</h2>
              <p className="ssp-verify-subtitle">
                {phoneReadOnly
                  ? 'Send a 6-digit verification code to your registered phone number.'
                  : 'Enter your phone number to receive a 6-digit verification code via SMS.'}
              </p>

              <div className="ssp-verify-form">
                <label className="ssp-field-label">Phone Number</label>
                <div className={`ssp-phone-row${phoneReadOnly ? ' ssp-phone-row--readonly' : ''}`}>
                  <select
                    className="ssp-country-select"
                    value={selectedCountry.code}
                    disabled={phoneReadOnly}
                    onChange={e => {
                      const country = COUNTRIES.find(c => c.code === e.target.value);
                      if (country) setSelectedCountry(country);
                      setPhoneInput('');
                    }}
                  >
                    {COUNTRIES.map((c, i) => (
                      <option key={`${c.code}-${c.name}-${i}`} value={c.code}>
                        {c.flag} {c.name} ({c.code})
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    className="ssp-input ssp-phone-input"
                    placeholder="Phone number"
                    value={phoneInput}
                    readOnly={phoneReadOnly}
                    onChange={e => !phoneReadOnly && setPhoneInput(e.target.value.replace(/\D/g, '').slice(0, 15))}
                    maxLength={15}
                    onKeyDown={e => e.key === 'Enter' && handleSendPhoneOtp()}
                  />
                </div>
                <button
                  className="ssp-btn-primary"
                  onClick={handleSendPhoneOtp}
                  disabled={actionLoading === 'send-otp' || phoneInput.replace(/\D/g, '').length < (selectedCountry.code === '+1' ? 10 : 7)}
                >
                  {actionLoading === 'send-otp' ? 'Sending...' : 'Send Verification Code'}
                </button>
              </div>
            </div>
          )}

          {/* ══ OTP verification view ══ */}
          {view === 'phone-otp' && (
            <div className="ssp-verify-flow">
              <button className="ssp-back-btn" onClick={() => { setView('phone-input'); setMsg({ type: '', text: '' }); }}>
                <span>←</span><span>Back</span>
              </button>
              <h2 className="ssp-verify-title">Enter Verification Code</h2>
              <p className="ssp-verify-subtitle">
                We sent a 6-digit code to {maskPhone(displayPhone)}. Enter it below.
              </p>

              <div className="ssp-otp-group">
                {otpDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    className={`ssp-otp-box${digit ? ' filled' : ''}`}
                    value={digit}
                    onChange={e => handleOtpInput(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    onFocus={e => e.target.select()}
                  />
                ))}
              </div>

              <button
                className="ssp-btn-primary"
                onClick={handleVerifyPhoneOtp}
                disabled={actionLoading === 'verify-otp' || otpDigits.join('').length < OTP_LENGTH}
              >
                {actionLoading === 'verify-otp' ? 'Verifying...' : 'Verify Code'}
              </button>

              <div className="ssp-resend">
                {resendTimer > 0 ? (
                  <span>Resend code in <strong>{resendTimer}s</strong></span>
                ) : (
                  <button
                    className="ssp-resend-btn"
                    onClick={handleResendOtp}
                    disabled={actionLoading === 'resend-otp'}
                  >
                    {actionLoading === 'resend-otp' ? 'Sending...' : 'Resend Code'}
                  </button>
                )}
              </div>

              <div className="ssp-change-phone">
                Wrong number?{' '}
                <button className="ssp-inline-link" onClick={goToPhoneInput}>Change phone number</button>
              </div>
            </div>
          )}

          {/* ══ Trust device prompt view ══ */}
          {view === 'trust-prompt' && (
            <div className="ssp-verify-flow">
              <div className="ssp-trust-success">
                <div className="ssp-trust-success-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <h2 className="ssp-trust-success-title">Phone Verified & MFA Enabled</h2>
                <p className="ssp-trust-success-subtitle">Your account is now protected with multi-factor authentication.</p>
              </div>

              <div className="ssp-trust-card">
                <div className="ssp-trust-card-header">
                  <div className="ssp-card-icon" style={{ width: 36, height: 36 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2"/>
                      <line x1="8" y1="21" x2="16" y2="21"/>
                      <line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                  </div>
                  <span className="ssp-trust-card-title">Trust This Browser?</span>
                </div>
                <p className="ssp-trust-card-desc">
                  Skip OTP verification on future logins from this browser.
                </p>
                <div className="ssp-trust-actions">
                  <button className="ssp-btn-secondary" onClick={handleSkipTrust}>
                    Not Now
                  </button>
                  <button
                    className="ssp-btn-primary"
                    onClick={handleTrustCurrentDevice}
                    disabled={actionLoading === 'trust-device'}
                  >
                    {actionLoading === 'trust-device' ? 'Trusting...' : 'Trust This Browser'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>

      {/*
        Required by Firebase RecaptchaVerifier for invisible reCAPTCHA.
        Must stay an empty, unstyled element (this matches Firebase's own docs) —
        `display: none` removes it from the render tree, which can stop the widget
        (or the classic reCAPTCHA v2 challenge it silently falls back to when
        Enterprise isn't provisioned for this domain) from ever rendering or
        resolving, leaving verifyPhoneNumber() hung indefinitely.
      */}
      <div id="recaptcha-container" />

      {/* Re-auth prompt for MFA disable (Firebase requires a recent sign-in) */}
      {reauthVisible && (
        <div
          className="ssp-reauth-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !reauthSubmitting) handleReauthCancel();
          }}
        >
          <div className="ssp-reauth-card" role="dialog" aria-modal="true">
            <div className="ssp-reauth-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <h3 className="ssp-reauth-title">Confirm Password</h3>
            <p className="ssp-reauth-body">
              For security, please re-enter your password to disable MFA.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleReauthSubmit();
              }}
            >
              <input
                type="password"
                className={`ssp-reauth-input${reauthError ? ' ssp-reauth-input--error' : ''}`}
                placeholder="Password"
                value={reauthPassword}
                onChange={(e) => {
                  setReauthPassword(e.target.value);
                  if (reauthError) setReauthError('');
                }}
                disabled={reauthSubmitting}
                autoFocus
              />
              {reauthError && <p className="ssp-reauth-error">{reauthError}</p>}
              <button
                type="submit"
                className="ssp-reauth-btn ssp-reauth-btn--primary"
                disabled={reauthSubmitting}
              >
                {reauthSubmitting ? 'Confirming...' : 'Confirm'}
              </button>
              <button
                type="button"
                className="ssp-reauth-btn ssp-reauth-btn--text"
                onClick={handleReauthCancel}
                disabled={reauthSubmitting}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
